import { Component, Injector, OnInit, Input, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { ComponentCanDeactivate } from 'src/app/core/route-guards/pending-changes.guard';
import { KeyDatesComponent } from 'src/app/modules/key-dates/components/key-dates.component';
import { Borrower, ApplicationContext, LoanApplication, LoanStatus, SubStatus } from 'src/app/models';
import { CustomData } from 'src/app/models/loan/custom-data.model';
import { UserType } from 'src/app/models/user/user-type.enum';
import { KeyDateType } from 'src/app/models/key-dates/key-date-types.enum';
import { MortgageCalculationDetails } from 'src/app/models/mortgage-calculation-details.model';
import { CustomDataInfoComponent } from 'src/app/modules/custom-data-info/custom-data-info.component';
import { UrlaMortgage } from 'src/app/modules/urla/models/urla-mortgage.model';
import { ApplicationContextBoundComponent } from 'src/app/shared/components/application-context-bound.component';
import { TpoConfiguration } from 'src/app/modules/admin/tpo-config/models/tpo-configuration.model';
import { LoanService } from 'src/app/services/loan';
import { MenuService } from 'src/app/services/menu.service';
import { NotificationService } from 'src/app/services/notification.service';
import { UrlaValidationService } from 'src/app/services/urla-validation.service';
import {
  TpoLoanSummarySectionConfig,
  TpoLoanSummarySectionId,
  mergeTpoLoanSummarySections,
} from 'src/app/modules/admin/tpo-config/models/tpo-loan-summary-sections';

@Component({
    selector: 'tpo-loan-summary',
    templateUrl: 'tpo-loan-summary.component.html',
    styleUrls: ['./tpo-loan-summary.component.scss'],
    standalone: false
})
export class TpoLoanSummaryComponent extends ApplicationContextBoundComponent implements OnInit, ComponentCanDeactivate {

  @ViewChild('customInfoForm')
  customInfoForm: NgForm;

  @ViewChild('customDataInfo')
  customDataInfo: CustomDataInfoComponent;

  @ViewChild(KeyDatesComponent)
  keyDates?: KeyDatesComponent;

  application: LoanApplication;
  mortgage: UrlaMortgage;
  mortgageCalculationDetails: MortgageCalculationDetails;
  borrowers: Borrower[];

  loanStatus: LoanStatus;
  subStatus: SubStatus;

  customData: CustomData[];

  /** Ordered, visible, eligible sections for the current loan's channel. Recomputed on context change. */
  orderedSections: TpoLoanSummarySectionConfig[] = [];

  @Input()
  isTpoSubmitted: boolean = false;

  protected isPRMG: boolean = false;

  protected isReverse: boolean = false;

  protected showCustomInfo: boolean = false;

  protected readonly userType: UserType = UserType.Tpo;

  protected customInfoSectionTitle: string = 'Custom Info';

  /** Field ids hidden for the loan's channel by the TPO config's Loan Summary Fields setting. */
  protected hiddenFields: string[] = [];

  protected isSavingCustomInfo: boolean = false;

  protected isLoanReadOnly: boolean = false;

  private tpoConfig: TpoConfiguration;

  private _customInfoFormValueChangesSubscription: Subscription;

  getWidthClass = (width: number): string => `section-col-${width}`;

  private _applicationContextSubscription: Subscription;

  private _mortgageCalculationDetailsSubscription: Subscription;

  constructor(
    private readonly _loanService: LoanService,
    private readonly _menuService: MenuService,
    private readonly _notifyService: NotificationService,
    private readonly _urlaValidationService: UrlaValidationService,
    private readonly injector: Injector) {
    super(injector);
  }

  // The embedded Key Dates section owns the unsaved-change check; without it there is nothing to protect.
  canDeactivate = (): boolean => !this.keyDates?.hasUnsavedChanges();

  confirm = (): boolean | Observable<boolean> => this.keyDates ? this.keyDates.confirm() : true;

  ngOnInit() {
    this.loadLoanRelatedData(this.applicationContext);

    this._applicationContextSubscription =
      this.applicationContextService.loanInfoChanges.subscribe(({ context }) => {
        this.loadLoanRelatedData(context);
      });

    this._mortgageCalculationDetailsSubscription = this.applicationContextService.mortgageCalculationDetailsChangedEvent.subscribe((context) => {
      this.mortgageCalculationDetails = context.currentMortgageCalculationDetails;
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this._applicationContextSubscription) {
      this._applicationContextSubscription.unsubscribe();
    }
    this._mortgageCalculationDetailsSubscription?.unsubscribe();
    this._customInfoFormValueChangesSubscription?.unsubscribe();
  }

  protected onCustomDataInfoInitialized = () => {
    // Surface the required-field messages up front, the way the loan summary page for GoProcessing does.
    setTimeout(() => {
      this.customInfoForm?.form.markAllAsTouched();
      this.publishCustomInfoStatus();

      this._customInfoFormValueChangesSubscription?.unsubscribe();
      this._customInfoFormValueChangesSubscription = this.customInfoForm?.valueChanges?.subscribe(() => {
        this.publishCustomInfoStatus();
      });
    }, 100);
  };

  protected saveCustomInfo = () => {
    this.customInfoForm?.form.markAllAsTouched();
    if (this.customInfoForm?.invalid) {
      this._notifyService.showError('Please fill out all required fields before saving.', 'Error!');
      return;
    }

    this.isSavingCustomInfo = true;
    this._loanService.saveLoanCustomData(this.application.applicationId, this.customData).subscribe({
      next: async (customData) => {
        this.customData = customData;
        this.isSavingCustomInfo = false;
        await this.applicationContextService.updateMortgageAndApplicationAsync(this.mortgage, this.application, customData);
        this.publishCustomInfoStatus();
        this._notifyService.showSuccess('Your changes have been saved successfully.', 'Success!');
      },
      error: (error) => {
        this.isSavingCustomInfo = false;
        this._notifyService.showError(error?.error?.message || 'Unable to save your changes.', 'Error!');
      }
    });
  };

  /** Keeps the Loan Summary menu step in sync with the required custom fields rendered on this page. */
  private publishCustomInfoStatus = () => {
    if (!this.showCustomInfo || !this.customDataInfo) {
      return;
    }
    const status = this._urlaValidationService.getRequiredCustomDataStatus(
      this.customDataInfo.customDataConfig, this.customData);
    this._menuService.setStatus('loanSummary', status);
  };

  private loadLoanRelatedData = (context: ApplicationContext) => {
    this.isPRMG = context.isCompanyPRMG;
    this.isReverse = context.isCompanySmartfi;
    this.isLoanReadOnly = context.applicationIsReadOnly;

    if (context.application) {
      this.application = context.application;
      this.mortgage = context.currentMortgage;
      this.mortgageCalculationDetails = context.currentMortgageCalculationDetails;
      const loanStatus = context.globalConfig.loanStatus.find(s => s.loanStatusId == this.application.loanStatusId);
      this.subStatus = context.globalConfig.subStatus.find(s => s.subStatusId == this.application.subStatusId && s.showOnTpoPortal);
      this.loanStatus = loanStatus;

      this.isTpoSubmitted = context.applicationKeyDatesByType
        ? !!context.applicationKeyDatesByType[KeyDateType.TpoSubmission] : false;
    }
    if (context.borrowers) {
      this.borrowers = context.borrowers;
    }
    this.tpoConfig = context.tpoConfig;
    this.hiddenFields = (this.application?.channel
      && this.tpoConfig?.loanSummaryHiddenFieldsByChannel?.[this.application.channel]) || [];
    this.computeOrderedSections();

    this.customData = context.customData;
    this.showCustomInfo = !!this.tpoConfig?.showCustomInfoOnTPOLoanSummary;
    this.customInfoSectionTitle = this.tpoConfig?.customInfoSectionTitle || 'Custom Info';
  };

  /** Saved per-channel sections for the loan's channel, or null to fall back to defaults. */
  private getChannelSections(): TpoLoanSummarySectionConfig[] | null {
    if (!this.tpoConfig || !this.application) {
      return null;
    }
    switch (this.application.channel) {
      case 'Wholesale': return this.tpoConfig.wholesaleLoanSummarySections;
      case 'Correspondent': return this.tpoConfig.corrLoanSummarySections;
      case 'NonDelegatedCorrespondent': return this.tpoConfig.nonDelLoanSummarySections;
      default: return null;
    }
  }

  /** Whether a section is eligible to render at all (independent of the visibility toggle). */
  private isSectionEligible(section: TpoLoanSummarySectionId): boolean {
    switch (section) {
      case 'Borrowers': return !!(this.borrowers && this.borrowers.length >= 1);
      // Closing Info is hard-hidden for PRMG Correspondent regardless of config (business rule wins).
      case 'ClosingInfo': return !(this.isPRMG && this.application?.channel === 'Correspondent');
      default: return true;
    }
  }

  private computeOrderedSections(): void {
    this.orderedSections = mergeTpoLoanSummarySections(this.getChannelSections())
      .filter(s => s.isVisible && this.isSectionEligible(s.section));
  }
}
